import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Heading, IconCheckmark } from '../../components';
import classNames from 'classnames';
import { buildEquipmentItemsByGroup, getEquipmentSummary } from './listingSectionUtils';

import css from './ListingPage.module.css';

const DISPLAY_LIMIT = 8;

const SectionEquipment = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration } = props;

  const intl = useIntl();
  const [equipmentExpanded, setEquipmentExpanded] = useState(false);
  const { equipmentFields, totalAmenities, hasEquipment } = useMemo(
    () =>
      getEquipmentSummary({
        publicData,
        metadata,
        listingFieldConfigs,
        categoryConfiguration,
      }),
    [publicData, metadata, listingFieldConfigs, categoryConfiguration]
  );

  if (!hasEquipment) return null;

  const equipmentItemsByGroup = useMemo(
    () => buildEquipmentItemsByGroup(intl, equipmentFields),
    [equipmentFields, intl]
  );

  const limitedEquipmentGroups = useMemo(() => {
    if (equipmentExpanded) {
      return equipmentItemsByGroup;
    }
    let remaining = DISPLAY_LIMIT;
    return equipmentItemsByGroup.map(group => {
      if (remaining <= 0) {
        return { ...group, items: [] };
      }
      const items = group.items.slice(0, remaining);
      remaining -= items.length;
      return { ...group, items };
    });
  }, [equipmentItemsByGroup, equipmentExpanded]);

  if (!equipmentFields || totalAmenities === 0) {
    return null;
  }

  return (
    <div className={css.customFields} id="listing-equipment">
      <Heading as="h2" rootClassName={classNames(css.sectionHeading, css.sectionHeadingColored)}>
        <FormattedMessage id="SectionEquipment.title" />
      </Heading>
      <div className={css.sectionSubheading}>
        <FormattedMessage id="SectionEquipment.subtitle" />
      </div>
      <div className={css.equipmentGroups}>
        {limitedEquipmentGroups.map(group =>
          group.items.length > 0 ? (
            <div key={group.key} className={css.equipmentGroup}>
              <div className={css.equipmentGroupTitle}>
                <FormattedMessage id={group.titleId} />
              </div>
              <ul className={css.equipmentList}>
                {group.items.map(item => (
                  <li key={item} className={css.equipmentListItem}>
                    <IconCheckmark size="small" className={css.equipmentCheck} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null
        )}
      </div>
      {totalAmenities > DISPLAY_LIMIT ? (
        <button
          type="button"
          className={css.equipmentToggle}
          onClick={() => setEquipmentExpanded(prev => !prev)}
        >
          {equipmentExpanded ? (
            <FormattedMessage id="SectionEquipment.showLess" />
          ) : (
            <FormattedMessage id="SectionEquipment.showAll" values={{ count: totalAmenities }} />
          )}
        </button>
      ) : null}
    </div>
  );
};

export default SectionEquipment;
